import { Banner } from '@ingenico/react-ui/banner';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { FileSelect, type FileWithStatus } from '@ingenico/react-ui/file-handlerV2';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import { LinearProgress, TextField } from '@mui/material';
import { toString } from 'lodash-es';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { FileUploadItemPreview } from '../file-upload-item-preview/FileUploadItemPreview.ctrl';
import { useCreateResourceModalVM } from './useCreateResourceModal.vm';
import styles from './CreateResourceModal.module.scss';

interface CreateResourceModalProps {
  readonly onCreated?: () => void;
}

export function CreateResourceModal({ onCreated }: CreateResourceModalProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.RESOURCES, ALL_LNGS_NAMESPACES.COMMON]);
  const {
    form,
    isOpen,
    isSubmitting,
    hasSubmitError,
    submitErrorMessage,
    isCapabilitiesLoading,
    capabilitiesError,
    fileExtensions,
    maxFileSize,
    unitFileSize,
    displayedMaxFileSize,
    createButtonDisabled,
    onClose,
    handleCreate,
    handleUploadingProgress,
    handleFilesChange,
    handleRemoveSelectedFile,
    handleFileDialogCancel,
  } = useCreateResourceModalVM({ onCreated });
  const { control } = form;

  return (
    <BasicDialogModal
      isOpen={isOpen}
      title={t('title.createResource', { ns: ALL_LNGS_NAMESPACES.RESOURCES })}
      onClose={onClose}
      hasCloseIcon
      hasDivider
      customPaperClassName={styles.paperContainer}
      actions={
        <div className={styles.modalActions}>
          <CoreButton variant="text" onClick={onClose} disabled={isSubmitting}>
            {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
          <CoreButton
            variant="contained"
            data-testid="btn-create-resource"
            loading={isSubmitting}
            startIcon={!hasSubmitError ? <AddIcon /> : <ReplayIcon />}
            onClick={() => {
              void handleCreate();
            }}
            disabled={createButtonDisabled}
          >
            {hasSubmitError
              ? t('button.retry', { ns: ALL_LNGS_NAMESPACES.COMMON })
              : t('button.create', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
        </div>
      }
    >
      {isCapabilitiesLoading ? (
        <LinearProgress />
      ) : capabilitiesError ? (
        <Banner message={t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON })} />
      ) : (
        <div className={styles.modalContent}>
          {hasSubmitError ? <Banner message={submitErrorMessage} /> : undefined}

          <Controller
            control={control}
            name={'files'}
            render={({ field, fieldState, formState }) => {
              const selectedResourceFile = field.value?.[0];
              const isFilesTouched = !!formState.touchedFields.files || formState.isSubmitted;
              const hasFileRequiredError = isFilesTouched && !!fieldState.error;
              const fileRequiredErrorMessage = hasFileRequiredError
                ? toString(t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON }))
                : undefined;
              const fileHandlerErrorMessage = selectedResourceFile?.error
                ? toString(
                    t([`error.file.${selectedResourceFile.error}`, 'error.default'], {
                      ns: ALL_LNGS_NAMESPACES.COMMON,
                    }),
                  )
                : undefined;

              return (
                <FileSelect
                  fileConfiguration={{
                    isMultiFiles: false,
                    autoCrop: false,
                    maxFileSize,
                    unitFileSize,
                    fileExtensions,
                    maxFiles: 1,
                  }}
                  filesToUpload={field.value ?? []}
                  fileItemPreviewVariant="default"
                  fileSelectVariant="default"
                  disabled={isSubmitting || isCapabilitiesLoading || !!field.value?.length}
                  shouldProcessBase64={false}
                  selectMainTitleText={t('field.upload.selectMainTitleText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                  selectFormatsText={t('field.upload.selectFormatsText', {
                    ns: ALL_LNGS_NAMESPACES.COMMON,
                    formats: fileExtensions.join(', '),
                  })}
                  selectSizeText={t('field.upload.selectSizeText', {
                    ns: ALL_LNGS_NAMESPACES.COMMON,
                    size: displayedMaxFileSize,
                  })}
                  selectBrowseText={t('field.upload.selectBrowseText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                  globalErrorMessageText={fileHandlerErrorMessage ?? fileRequiredErrorMessage}
                  onFilesChange={(uploadedFiles: FileWithStatus[]) => handleFilesChange(uploadedFiles, field.onChange)}
                  onRemoveFile={() => handleRemoveSelectedFile()}
                  onFileDialogCancel={handleFileDialogCancel}
                  customFileItemPreview={(file) => (
                    <FileUploadItemPreview
                      previewSizeBase={2}
                      file={file}
                      hasError={!!selectedResourceFile?.error || hasFileRequiredError}
                      onRemove={() => handleRemoveSelectedFile()}
                    />
                  )}
                  hasError={!!selectedResourceFile?.error || hasFileRequiredError}
                  getUploadingProgress={handleUploadingProgress}
                  required
                />
              );
            }}
          />

          <Controller
            control={control}
            name={'name'}
            render={({ field, fieldState, formState }) => {
              const isNameTouched = !!formState.touchedFields.name || formState.isSubmitted;
              const hasNameError = isNameTouched && !!fieldState.error;

              return (
                <TextField
                  value={field.value ?? ''}
                  label={t('field.name', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                  onChange={(event) => field.onChange(event.target.value)}
                  required
                  error={hasNameError}
                  helperText={hasNameError ? toString(fieldState.error?.message) : ''}
                  disabled={isSubmitting}
                  fullWidth
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                />
              );
            }}
          />

          <Controller
            control={control}
            name={'description'}
            render={({ field }) => (
              <TextField
                value={field.value ?? ''}
                label={t('field.description', { ns: ALL_LNGS_NAMESPACES.RESOURCES })}
                onChange={(event) => field.onChange(event.target.value)}
                disabled={isSubmitting}
                multiline
                minRows={3}
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            )}
          />
        </div>
      )}
    </BasicDialogModal>
  );
}
